import { arcWpsPath, WaypointCoordinateLike } from './arc-wps-path';

type LocationLike = {
  latitude?: string | number | null;
  longitude?: string | number | null;
};

type HomeCandidate = {
  label?: string;
  type?: string | null;
};

const isHomeWaypoint = (w: HomeCandidate): boolean =>
  w.type === 'HOME' || w.label === 'H';

const splitHomeLeg = <T extends HomeCandidate & WaypointCoordinateLike>(
  waypoints: T[],
): { homePoint: T | null; routeWaypoints: T[] } => {
  const typedHome = waypoints.filter((w) => isHomeWaypoint(w));
  if (typedHome.length > 0) {
    return { homePoint: typedHome[0], routeWaypoints: waypoints.filter((w) => !isHomeWaypoint(w)) };
  }

  const [first, ...rest] = waypoints;
  return { homePoint: first ?? null, routeWaypoints: rest };
};

export type HistoryOrderPathLike = {
  waypoints?: WaypointCoordinateLike[];
  takeoffLocation?: LocationLike;
  deliveryLocation?: LocationLike;
  destinationLocation?: LocationLike;
  drone?: LocationLike;
  orders?: Array<{
    takeoffLocation?: LocationLike;
    deliveryLocation?: LocationLike;
    destinationLocation?: LocationLike;
  }>;
};

export type HistoryDroneHomeLeg = {
  from: google.maps.LatLngLiteral;
  to: google.maps.LatLngLiteral;
};

const toLatLng = (location?: LocationLike): google.maps.LatLngLiteral | null => {
  if (!location) return null;
  const lat = Number(location.latitude);
  const lng = Number(location.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

const getTakeoffPoint = (order: HistoryOrderPathLike): google.maps.LatLngLiteral | null =>
  toLatLng(order.takeoffLocation)
  ?? toLatLng(order.orders?.[0]?.takeoffLocation);

const getDestinationPoint = (order: HistoryOrderPathLike): google.maps.LatLngLiteral | null =>
  toLatLng(order.deliveryLocation)
  ?? toLatLng(order.destinationLocation)
  ?? toLatLng(order.orders?.[0]?.deliveryLocation)
  ?? toLatLng(order.orders?.[0]?.destinationLocation);

export const arcHistoryTakeoffPoint = (
  order: HistoryOrderPathLike | null | undefined,
): google.maps.LatLngLiteral | null => {
  if (!order) return null;

  if (order.waypoints?.length) {
    const { routeWaypoints } = splitHomeLeg(order.waypoints);
    const fromWaypoints = routeWaypoints[0] ? toLatLng(routeWaypoints[0]) : null;
    if (fromWaypoints) return fromWaypoints;
  }

  return getTakeoffPoint(order) ?? arcHistoryOrderPath(order)[0] ?? null;
};

export const arcHistoryDroneHomeLeg = (
  order: HistoryOrderPathLike | null | undefined,
): HistoryDroneHomeLeg | null => {
  if (!order) return null;

  let dronePos: google.maps.LatLngLiteral | null = null;
  let takeoffPos: google.maps.LatLngLiteral | null = null;

  if (order.waypoints?.length) {
    const { homePoint, routeWaypoints } = splitHomeLeg(order.waypoints);
    dronePos = homePoint ? toLatLng(homePoint) : null;
    takeoffPos = routeWaypoints[0] ? toLatLng(routeWaypoints[0]) : null;
  }

  if (!dronePos) {
    dronePos = toLatLng(order.drone);
  }

  if (!takeoffPos) {
    takeoffPos = getTakeoffPoint(order);
  }

  if (!dronePos || !takeoffPos) return null;
  return { from: dronePos, to: takeoffPos };
};

export const arcHistoryDronePoint = (
  order: HistoryOrderPathLike | null | undefined,
): google.maps.LatLngLiteral | null =>
  arcHistoryDroneHomeLeg(order)?.from ?? toLatLng(order?.drone);

export const arcHistoryDestinationPoint = (
  order: HistoryOrderPathLike | null | undefined,
): google.maps.LatLngLiteral | null => (order ? getDestinationPoint(order) : null);

export const arcHistoryOrderPath = (
  order: HistoryOrderPathLike | null | undefined,
): google.maps.LatLngLiteral[] => {
  if (!order) return [];

  if (order.waypoints?.length) {
    const { routeWaypoints } = splitHomeLeg(order.waypoints);
    const waypointPath = arcWpsPath(routeWaypoints);
    if (waypointPath.length >= 2) return waypointPath;
  }

  const takeoff = getTakeoffPoint(order);
  const destination = getDestinationPoint(order);
  if (takeoff && destination) return [takeoff, destination];

  return [];
};
